import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, Info, Sparkles, Download, FileText, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { notify } from '@/components/core';
import { useAuth } from '@/context/useAuth';
import { apiFetch } from '../../../api';

interface PivotNodeConfigProps {
    data: any;
    onChange: (key: string, value: any) => void;
    inputColumns: any[];
    flowId?: string;
    nodeId?: string;
}

export const PivotNodeConfig: React.FC<PivotNodeConfigProps> = ({ data, onChange, inputColumns, flowId, nodeId }) => {
    const { token } = useAuth();
    const groupBy = data.group_by || [];
    const pivotValues = data.pivot_values || [];
    const [csvInput, setCsvInput] = useState("");
    const [showCsv, setShowCsv] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [isAiAnalyzing, setIsAiAnalyzing] = useState(false);

    const handleGroupChange = (colName: string, checked: boolean) => {
        const newGroups = checked
            ? [...groupBy, colName]
            : groupBy.filter((g: any) => g !== colName);
        onChange('group_by', newGroups);
    };

    const addPivotValue = () => {
        onChange('pivot_values', [...pivotValues, '']);
    };

    const removePivotValue = (index: number) => {
        const newValues = [...pivotValues];
        newValues.splice(index, 1);
        onChange('pivot_values', newValues);
    };

    const updatePivotValue = (index: number, val: string) => {
        const newValues = [...pivotValues];
        newValues[index] = val;
        onChange('pivot_values', newValues);
    };

    const handleCsvImport = () => {
        if (!csvInput.trim()) return;

        // Split by comma, newline, or tab
        const values = csvInput.split(/[\n,\t]+/).map(v => v.trim()).filter(v => v);

        // Merge unique
        const currentSet = new Set(pivotValues);
        let added = 0;
        values.forEach(v => {
            if (!currentSet.has(v)) {
                pivotValues.push(v);
                currentSet.add(v);
                added++;
            }
        });

        onChange('pivot_values', [...pivotValues]);
        setCsvInput("");
        setShowCsv(false);
        notify.success(`Imported ${added} values.`);
    };

    const handleAutoFetch = async () => {
        if (!flowId || !nodeId) {
            notify.error("Flow context missing. Please save and refresh.");
            return;
        }
        if (!data.pivot_column) {
            notify.warning("Please select a Pivot Column first.");
            return;
        }

        setIsFetching(true);
        try {
            const json = await apiFetch(`/flows/${flowId}/inspect-column-values`, null, {
                method: 'POST',
                body: JSON.stringify({
                    nodeId: nodeId,
                    column: data.pivot_column,
                    limit: 50
                })
            });
            const fetchedValues = (json as any)?.values || [];

            if (fetchedValues.length === 0) {
                notify.info("No distinct values found.", {
                    description: "Try running the flow up to the previous node first."
                });
                return;
            }

            // Merge unique
            const currentSet = new Set(pivotValues);
            let added = 0;
            fetchedValues.forEach((v: string) => {
                if (!currentSet.has(v)) {
                    pivotValues.push(v);
                    currentSet.add(v);
                    added++;
                }
            });

            onChange('pivot_values', [...pivotValues]);
            notify.success(`Fetched ${fetchedValues.length} values. Added ${added} new.`);

        } catch (error: any) {
            notify.error("Auto-Fetch Failed", { description: error.message });
        } finally {
            setIsFetching(false);
        }
    };

    const handleAiSuggest = async () => {
        if (inputColumns.length === 0) {
            notify.warning("No input columns to analyze.");
            return;
        }

        setIsAiAnalyzing(true);
        try {
            const suggestion = await apiFetch('/ai/suggest-pivot', null, {
                method: 'POST',
                body: JSON.stringify({
                    columns: inputColumns,
                    goal: "pivot"
                })
            });

            // Apply suggestions if valid
            let changes = 0;
            if ((suggestion as any).group_by && Array.isArray((suggestion as any).group_by)) {
                onChange('group_by', (suggestion as any).group_by);
                changes++;
            }
            if ((suggestion as any).pivot_column) {
                onChange('pivot_column', (suggestion as any).pivot_column);
                changes++;
            }
            if ((suggestion as any).agg_column) {
                onChange('agg_column', (suggestion as any).agg_column);
                changes++;
            }
            if ((suggestion as any).agg_function) {
                onChange('agg_function', (suggestion as any).agg_function);
                changes++;
            }
            if ((suggestion as any).pivot_values && Array.isArray((suggestion as any).pivot_values)) {
                onChange('pivot_values', (suggestion as any).pivot_values);
                changes++;
            }

            notify.success("AI Suggestions Applied", {
                description: `Updated ${changes} configuration fields based on column analysis.`
            });

        } catch (error: any) {
            notify.error("AI Suggest Failed", { description: error.message });
        } finally {
            setIsAiAnalyzing(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 text-xs text-blue-800 flex gap-2">
                <Info className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                    <strong>Pivot Operation:</strong> Reshapes rows into columns.
                    <ul className="list-disc ml-4 mt-1 space-y-1">
                        <li>Each unique value in the <b>Pivot Column</b> becomes a new column header.</li>
                        <li>Data is aggregated using the selected <b>Aggregation</b>.</li>
                        <li className="text-blue-600 font-medium">SQL Server / Standard SQL requires explicit column names.</li>
                    </ul>
                </div>
            </div>

            {/* Group By Selection */}
            <div className="space-y-3">
                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Rows / Identifiers</Label>
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50 p-2 space-y-1">
                    {inputColumns.length === 0 ? (
                        <div className="text-xs text-gray-400 italic p-2">No input columns available.</div>
                    ) : (
                        inputColumns.map((col) => {
                            const isChecked = groupBy.includes(col.name);
                            return (
                                <div key={col.name} className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                        checked={isChecked}
                                        onChange={(e) => handleGroupChange(col.name, e.target.checked)}
                                    />
                                    <span className="text-[11px] text-gray-700 truncate" title={col.name}>{col.name}</span>
                                    <span className="text-[9px] text-gray-400 uppercase">({col.type})</span>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
                {/* Pivot Column Selection */}
                <div className="space-y-2">
                    <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Pivot Column</Label>
                    <div className="flex gap-2">
                        <Select
                            value={data.pivot_column || ''}
                            onValueChange={(v) => onChange('pivot_column', v)}
                        >
                            <SelectTrigger className="w-full text-xs">
                                <SelectValue placeholder="Select Column..." />
                            </SelectTrigger>
                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                {inputColumns.map(c => (
                                    <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button
                            variant="outline"
                            size="icon"
                            className="h-9 w-9 shrink-0"
                            onClick={handleAiSuggest}
                            disabled={isAiAnalyzing}
                            title="AI Suggest Column"
                        >
                            {isAiAnalyzing ? <Loader2 className="h-4 w-4 animate-spin text-purple-600" /> : <Sparkles className="h-4 w-4 text-purple-600" />}
                        </Button>
                    </div>
                </div>

                {/* Aggregation Selection */}
                <div className="space-y-2">
                    <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Value to Aggregate</Label>
                    <div className="flex gap-2">
                        <Select
                            value={data.agg_function || 'SUM'}
                            onValueChange={(v) => onChange('agg_function', v)}
                        >
                            <SelectTrigger className="w-24 text-xs font-medium">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                {['SUM', 'COUNT', 'MIN', 'MAX', 'AVG', 'FIRST', 'LAST'].map(f => (
                                    <SelectItem key={f} value={f}>{f}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Select
                            value={data.agg_column || ''}
                            onValueChange={(v) => onChange('agg_column', v)}
                        >
                            <SelectTrigger className="flex-1 text-xs">
                                <SelectValue placeholder="Select Value Column..." />
                            </SelectTrigger>
                            <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                {inputColumns.map(c => (
                                    <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            {/* Pivot Values (Manual Entry) */}
            <div className="space-y-3 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Pivot Values (New Columns)</Label>
                    <div className="flex gap-1">
                        <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 text-[10px] text-purple-600 hover:bg-purple-50 px-2"
                            onClick={handleAutoFetch}
                            disabled={isFetching}
                            title="Fetch distinct values from data"
                        >
                            {isFetching ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Download className="h-3 w-3 mr-1" />}
                            Auto-Fetch
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 text-[10px] text-blue-600 hover:bg-blue-50 px-2"
                            onClick={() => setShowCsv(!showCsv)}
                            title="Paste list of values"
                        >
                            <FileText className="h-3 w-3 mr-1" /> CSV Input
                        </Button>
                        <Button size="sm" variant="outline" className="h-6 text-[10px] py-0 px-2" onClick={addPivotValue}>
                            <Plus className="h-3 w-3 mr-1" /> Add
                        </Button>
                    </div>
                </div>

                {showCsv && (
                    <div className="space-y-2 p-2 bg-gray-50 rounded border border-gray-200 animate-in slide-in-from-top-2">
                        <Label className="text-[10px] text-gray-500">Paste values separated by comma or newlines:</Label>
                        <Textarea
                            className="text-xs h-20 bg-white"
                            value={csvInput}
                            onChange={(e) => setCsvInput(e.target.value)}
                            placeholder="Val1, Val2, Val3..."
                        />
                        <div className="flex justify-end gap-2">
                            <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setShowCsv(false)}>Cancel</Button>
                            <Button size="sm" className="h-6 text-xs" onClick={handleCsvImport}>Import</Button>
                        </div>
                    </div>
                )}

                <div className="space-y-2 max-h-40 overflow-y-auto">
                    {pivotValues.map((val: string, idx: number) => (
                        <div key={idx} className="flex items-center gap-2">
                            <Input
                                className="h-7 text-xs"
                                placeholder="e.g. 2023, Jan, Q1..."
                                value={val}
                                onChange={(e) => updatePivotValue(idx, e.target.value)}
                            />
                            <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50"
                                onClick={() => removePivotValue(idx)}
                            >
                                <Trash2 className="h-3 w-3" />
                            </Button>
                        </div>
                    ))}
                    {pivotValues.length === 0 && !showCsv && (
                        <div className="text-center py-4 text-xs text-gray-400 italic border border-dashed rounded bg-gray-50">
                            Add values manually or use Auto-Fetch / CSV Input.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
